import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { chakra, Box, Flex, Image, SimpleGrid, Text } from "@chakra-ui/react";

import LoginUser from "../components/LoginUser/LoginUser";

const NavLink = chakra(RouterLink);

type HeaderProps = {
  login: any;
};

const Header: React.FC<HeaderProps> = ({ login }) => {
  const isLoggedIn = login.status === "LOGGED_IN";
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(0);

  useEffect(() => {
    if (!headerRef.current) return;

    const element = headerRef.current;

    const updateHeight = () => {
      setHeaderHeight(element.getBoundingClientRect().height);
    };

    updateHeight();

    const observer = new ResizeObserver(() => {
      updateHeight();
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [isLoggedIn]);

  return (
    <>
      <Box h={headerHeight ? `${headerHeight}px` : undefined} />

      <Flex
        ref={headerRef}
        as="nav"
        fontFamily="montserrat"
        bg="#0b4800"
        color="white"
        justify="space-between"
        align="center"
        p={8}
        pos="fixed"
        top={0}
        left={0}
        w="100%"
        zIndex={20}
        wrap="nowrap"
        sx={{
          "@media (max-width: 1023px)": {
            flexDirection: "column",
            alignItems: "stretch",
            gap: "16px",
            padding: "16px",
          },
        }}
      >
        <Flex
          align="center"
          justify="center"
          flexShrink={0}
          sx={{
            "@media (min-width: 1024px)": {
              justifyContent: "flex-start",
            },
          }}
        >
          <Box>
            <Image
              src="/logoHeader.png"
              alt="Alice"
              h={{ base: "48px", md: "68px", lg: "76px" }}
              objectFit="contain"
            />
          </Box>
        </Flex>

        {isLoggedIn ? (
          <SimpleGrid
            columns={4}
            spacing={4}
            sx={{
              "@media (max-width: 1440px)": {
                gap: "8px",
              },
              "@media (max-width: 1023px)": {
                width: "100%",
                gridTemplateColumns: "1fr",
                gap: "12px",
              },
              "@media (min-width: 640px) and (max-width: 1023px)": {
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            <Box minW={0}>
              <Text color="blue.300" fontSize="md" fontWeight="semibold">
                Product Controller
              </Text>
              <NavLink _focus={{ boxShadow: "none" }} fontSize="md" pr={{ base: 0, lg: 4, xl: 6 }} to="/products/search">
                Search
              </NavLink>
              <NavLink _focus={{ boxShadow: "none" }} fontSize="md" pr={{ base: 0, lg: 4, xl: 6 }} to="/products">
                Product
              </NavLink>
            </Box>

            <Box minW={0}>
              <Text color="blue.300" fontSize="md" fontWeight="semibold">
                Supplier Controller
              </Text>
              <NavLink _focus={{ boxShadow: "none" }} fontSize="md" pr={{ base: 0, lg: 4, xl: 6 }} to="/suppliers/search">
                Search
              </NavLink>
              <NavLink _focus={{ boxShadow: "none" }} fontSize="md" pr={{ base: 0, lg: 4, xl: 6 }} to="/suppliers">
                Supplier
              </NavLink>
            </Box>

            <Box minW={0}>
              <Text color="blue.300" fontSize="md" fontWeight="semibold">
                Flow Controller
              </Text>
              <NavLink _focus={{ boxShadow: "none" }} fontSize="md" pr={{ base: 0, lg: 4, xl: 6 }} to="/flow">
                Checkout
              </NavLink>
            </Box>

            <Box minW={0}>
              <Text color="blue.300" fontSize="md" fontWeight="semibold">
                Booking &amp; Ticket Controllers
              </Text>
              <NavLink _focus={{ boxShadow: "none" }} fontSize="md" pr={{ base: 0, lg: 4, xl: 6 }} to="/booking">
                Bookings
              </NavLink>
              <NavLink _focus={{ boxShadow: "none" }} fontSize="md" pr={{ base: 0, lg: 4, xl: 6 }} to="/ticket">
                Tickets
              </NavLink>
            </Box>
          </SimpleGrid>
        ) : null}

        {isLoggedIn ? (
          <Box
            flexShrink={0}
            sx={{
              "@media (max-width: 1023px)": {
                width: "100%",
              },
            }}
          >
            <LoginUser login={login} />
          </Box>
        ) : null}
      </Flex>
    </>
  );
};

export default Header;